import { BaseQueryFn, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';
import { logOut, updateToken, updateRefreshToken } from '../store/authSlice';
import { Mutex } from 'async-mutex';
import { API_URL, LS_KEYS } from '../store/constants';

const mutex = new Mutex();

const authBaseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState, endpoint }) => {
        const state = getState() as RootState;
        const token = state.auth.accessToken;
        const tokenLS = localStorage.getItem(LS_KEYS.token);

        headers.set('Accept', 'application/json');
        
        // Для multipart endpoints НЕ устанавливаем Content-Type
        const isMultipartEndpoint = endpoint && (
            endpoint.includes('Import') || 
            endpoint.includes('Upload') ||
            endpoint === 'wellImport' ||
            endpoint === 'postChromatogram'
        );
        
        if (!isMultipartEndpoint && !headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
        
        if (token && token !== 'delete') {
            headers.set('Authorization', `Bearer ${token}`);
        } else if (token === 'delete') {
            headers.delete('Authorization');
        } else if (tokenLS) {
            headers.set('Authorization', `Bearer ${tokenLS}`);
        }

        return headers;
    },
    // Добавляем обработчик ответов для поддержки blob
    responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');
        
        // Если это PDF или ZIP файл, возвращаем blob
        if (contentType?.includes('application/pdf') || 
            contentType?.includes('application/zip') ||
            contentType?.includes('application/octet-stream') ||
            contentType?.includes('image/')) {
            return response.blob();
        }
        
        // Если это JSON, парсим как JSON
        if (contentType?.includes('application/json')) {
            const text = await response.text();
            return text ? JSON.parse(text) : null;
        }
        
        // Для других типов возвращаем текст
        return response.text();
    },
});

export const getBaseQuery: BaseQueryFn<string | FetchArgs, unknown, unknown> = async (
    args,
    api,
    extraOptions
) => {
    const { getState, dispatch } = api as any;
    const state = getState() as RootState;
    const token: any = (api.getState() as RootState).auth.accessToken;
    const refresh_token: any = (api.getState() as RootState).auth.refreshToken;

    await mutex.waitForUnlock();
    let result = await authBaseQuery(args, api, extraOptions);

    // Проверяем ошибку только если это не blob ответ
    const isBlobResponse = result.data instanceof Blob;
    
    if (result?.error && result.error.status === 401 && !isBlobResponse) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();

            try {
                const verifyRes = await verifyToken(token, api);
                if (verifyRes?.error) {
                    const refreshRes = await refreshToken(refresh_token, api);
                    //@ts-ignore
                    if (refreshRes?.data && refreshRes.data?.accessToken) {
                        //@ts-ignore
                        dispatch(updateToken({ accessToken: refreshRes.data.accessToken }));
                        //@ts-ignore
                        if (refreshRes.data?.refreshToken) {
                            //@ts-ignore
                            dispatch(updateRefreshToken({ refreshToken: refreshRes.data.refreshToken }));
                        }
                        result = await authBaseQuery(args, api, extraOptions);
                    } else {
                        handleLogout(dispatch);
                    }
                }
            } finally {
                release();
            }
        }
    }

    return result;
};

const verifyToken = async (token: string | undefined, api: any) => {
    if (!token) return { error: { status: 401 } };
    return await authBaseQuery(
        {
            url: '/auth/verify_token/',
            method: 'POST',
            body: { token },
        },
        api,
        {}
    );
};

const refreshToken = async (refresh_token: string | undefined, api: any) => {
    if (!refresh_token) return { error: { status: 401 } };
    return await authBaseQuery(
        {
            url: '/auth/refresh_token/',
            method: 'POST',
            headers: { 'Authorization': 'delete' },
            body: { refresh: refresh_token },
        },
        api,
        {}
    );
};

function handleLogout(dispatch: any) {
    dispatch(logOut());
}

// Создаем специальный базовый запрос для multipart данных
export const getMultipartBaseQuery: BaseQueryFn<string | FetchArgs, unknown, unknown> = async (
    args,
    api,
    extraOptions
) => {
    const { getState, dispatch } = api as any;
    const state = getState() as RootState;
    const token: any = (api.getState() as RootState).auth.accessToken;
    const refresh_token: any = (api.getState() as RootState).auth.refreshToken;

    await mutex.waitForUnlock();
    
    // Создаем временный baseQuery для multipart
    const multipartBaseQuery = fetchBaseQuery({
        baseUrl: API_URL,
        prepareHeaders: (headers, { getState }) => {
            const state = getState() as RootState;
            const token = state.auth.accessToken;
            const tokenLS = localStorage.getItem(LS_KEYS.token);
            
            headers.set('Accept', 'application/json');
            // НЕ устанавливаем Content-Type - браузер сам установит multipart/form-data
            
            if (token && token !== 'delete') {
                headers.set('Authorization', `Bearer ${token}`);
            } else if (token === 'delete') {
                headers.delete('Authorization');
            } else if (tokenLS) {
                headers.set('Authorization', `Bearer ${tokenLS}`);
            }

            return headers;
        },
    });

    let result = await multipartBaseQuery(args, api, extraOptions);

    // Проверяем ошибку только если это не blob ответ
    const isBlobResponse = result.data instanceof Blob;
    
    if (result?.error && result.error.status === 401 && !isBlobResponse) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();

            try {
                const verifyRes = await verifyToken(token, api);
                if (verifyRes?.error) {
                    const refreshRes = await refreshToken(refresh_token, api);
                    //@ts-ignore
                    if (refreshRes?.data && refreshRes.data?.accessToken) {
                        //@ts-ignore
                        dispatch(updateToken({ accessToken: refreshRes.data.accessToken }));
                        //@ts-ignore
                        if (refreshRes.data?.refreshToken) {
                            //@ts-ignore
                            dispatch(updateRefreshToken({ refreshToken: refreshRes.data.refreshToken }));
                        }
                        result = await multipartBaseQuery(args, api, extraOptions);
                    } else {
                        handleLogout(dispatch);
                    }
                }
            } finally {
                release();
            }
        }
    }

    return result;
};